import { useState } from 'react'
import { useAuthStore } from '../hooks/useAuth'
import {
  CreditCard, CheckCircle, X, Star, Zap, Shield, Crown,
  ArrowRight, Lock, Unlock, Calendar, DollarSign
} from 'lucide-react'
import toast from 'react-hot-toast'

interface Plan {
  id: string
  name: string
  price: number
  period: string
  features: string[]
  highlighted?: boolean
  icon: typeof Star
}

export default function SubscriptionManager() {
  const { user, updateUser } = useAuthStore()
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')

  const plans: Plan[] = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      period: 'forever',
      icon: Star,
      features: [
        'Access to 5 free courses',
        'Basic quizzes',
        'Community support',
        'Standard video quality',
        'Email notifications'
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      price: billingCycle === 'monthly' ? 19.99 : 199.99,
      period: billingCycle === 'monthly' ? 'month' : 'year',
      icon: Zap,
      highlighted: true,
      features: [
        'Unlimited course access',
        'AI-powered tutoring',
        'Priority support',
        'HD video streaming',
        'Downloadable resources',
        'Certificate generation',
        'Random quiz mode',
        'Progress analytics'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: billingCycle === 'monthly' ? 49.99 : 499.99,
      period: billingCycle === 'monthly' ? 'month' : 'year',
      icon: Crown,
      features: [
        'Everything in Premium',
        'Admin dashboard access',
        'Custom branding',
        'API access',
        'SSO integration',
        'Advanced analytics',
        'Dedicated support',
        'White-label option',
        'Bulk user management'
      ]
    }
  ]

  const handleSubscribe = (planId: string) => {
    setSelectedPlan(planId)
    setShowPaymentModal(true)
  }

  const processPayment = () => {
    // In production: integrate Stripe/PayPal
    toast.success(`Subscribed to ${selectedPlan?.toUpperCase()} plan!`)
    updateUser({ subscription: selectedPlan as any })
    setShowPaymentModal(false)
  }

  const currentPlan = plans.find(p => p.id === user?.subscription) || plans[0]

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold">Subscription Management</h1>
        <p className="text-slate-500 mt-1">Manage your plan and billing</p>
      </div>

      {/* Current Plan */}
      <div className="glass rounded-xl p-6 border border-primary/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Current Plan</p>
              <h3 className="text-xl font-bold">{currentPlan.name}</h3>
              <p className="text-sm text-slate-500">
                {currentPlan.price === 0 ? 'Free forever' : `$${currentPlan.price}/${currentPlan.period}`}
              </p>
            </div>
          </div>
          <span className="px-3 py-1 bg-success/20 text-success rounded-full text-sm font-medium">
            <CheckCircle className="w-3 h-3 inline mr-1" /> Active
          </span>
        </div>
      </div>

      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4">
        <span className={`text-sm ${billingCycle === 'monthly' ? 'text-white' : 'text-slate-500'}`}>Monthly</span>
        <button
          onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
          className="relative w-14 h-7 bg-slate-800 rounded-full transition-colors"
        >
          <div className={`absolute top-1 w-5 h-5 bg-primary rounded-full transition-transform ${
            billingCycle === 'yearly' ? 'translate-x-7' : 'translate-x-1'
          }`} />
        </button>
        <span className={`text-sm ${billingCycle === 'yearly' ? 'text-white' : 'text-slate-500'}`}>
          Yearly <span className="text-success text-xs">Save 20%</span>
        </span>
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map(plan => {
          const Icon = plan.icon
          const isCurrent = user?.subscription === plan.id
          return (
            <div
              key={plan.id}
              className={`glass rounded-xl p-6 relative ${
                plan.highlighted ? 'border-2 border-primary' : 'border border-slate-800'
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-white text-xs font-bold rounded-full">
                  MOST POPULAR
                </div>
              )}

              <div className="text-center mb-6">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 ${
                  plan.highlighted ? 'bg-primary/20' : 'bg-slate-800'
                }`}>
                  <Icon className={`w-6 h-6 ${plan.highlighted ? 'text-primary' : 'text-slate-400'}`} />
                </div>
                <h3 className="text-xl font-bold">{plan.name}</h3>
                <div className="mt-2">
                  <span className="text-3xl font-bold">${plan.price}</span>
                  <span className="text-slate-500">/{plan.period}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle className={`w-4 h-4 mt-0.5 shrink-0 ${
                      plan.highlighted ? 'text-primary' : 'text-slate-500'
                    }`} />
                    <span className="text-slate-400">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => !isCurrent && handleSubscribe(plan.id)}
                disabled={isCurrent}
                className={`w-full py-2.5 rounded-lg font-medium transition-colors ${
                  isCurrent
                    ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                    : plan.highlighted
                    ? 'bg-primary hover:bg-primary-dark'
                    : 'bg-slate-800 hover:bg-slate-700'
                }`}
              >
                {isCurrent ? 'Current Plan' : 'Subscribe'}
              </button>
            </div>
          )
        })}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="glass rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Complete Payment</h2>
              <button onClick={() => setShowPaymentModal(false)} className="p-1 hover:bg-slate-800 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-slate-800/50 rounded-lg">
              <p className="text-sm text-slate-500">Selected Plan</p>
              <p className="font-bold">{plans.find(p => p.id === selectedPlan)?.name}</p>
              <p className="text-2xl font-bold text-primary">
                ${plans.find(p => p.id === selectedPlan)?.price}
                <span className="text-sm text-slate-500 font-normal">/{plans.find(p => p.id === selectedPlan)?.period}</span>
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Card Number</label>
                <input
                  type="text"
                  placeholder="4242 4242 4242 4242"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Expiry</label>
                  <input
                    type="text"
                    placeholder="MM/YY"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">CVC</label>
                  <input
                    type="text"
                    placeholder="123"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary"
                  />
                </div>
              </div>
              <button
                onClick={processPayment}
                className="w-full py-3 bg-primary hover:bg-primary-dark rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <CreditCard className="w-4 h-4" />
                Pay Now
              </button>
            </div>

            <p className="text-xs text-slate-600 text-center mt-4">
              Secured by Stripe • Encrypted transaction
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
